import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, Button, Input, Label, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getHomeBanners, addHomeBanner, deleteHomeBanner } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const HomeBanner = () => {
  document.title = "Home Banner - Dashboard";
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await getHomeBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to load banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const toggle = () => {
    setModal(!modal);
    setLinkUrl("");
    setSortOrder(0);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error("Please select an image");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("bannerImage", imageFile);
      formData.append("link_url", linkUrl);
      formData.append("sort_order", sortOrder);
      await addHomeBanner(formData);
      toast.success("Banner added successfully");
      toggle();
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to add banner");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await deleteHomeBanner(id);
      toast.success("Banner deleted");
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || "Failed to delete banner");
    }
  };

  const getImageUrl = (banner) => {
    if (!banner.image) return null;
    if (banner.image.startsWith("http")) return banner.image;
    const base = (API_BASE || "").replace("/api", "");
    return `${base}${banner.image.startsWith("/") ? "" : "/"}${banner.image}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Home Banner" pageTitle="Website" />
        <Row>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Scroll Banners (Home Page)</h5>
                <Button color="primary" onClick={toggle}>
                  <i className="ri-add-line align-bottom me-1"></i> Upload Banner
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner /> Loading...
                </div>
              ) : banners.length === 0 ? (
                <p className="text-muted">No banners yet. Upload your first banner.</p>
              ) : (
                <Row>
                  {banners.map((b) => (
                    <Col md={6} lg={4} key={b.id} className="mb-3">
                      <div className="border rounded p-2 position-relative">
                        <img
                          src={getImageUrl(b)}
                          alt="Banner"
                          className="img-fluid rounded"
                          style={{ maxHeight: 160, objectFit: "cover", width: "100%" }}
                        />
                        <div className="mt-2 small text-muted">
                          {b.link_url && <div>Link: {b.link_url}</div>}
                          Order: {b.sort_order}
                        </div>
                        <Button
                          color="danger"
                          size="sm"
                          className="mt-2"
                          onClick={() => handleDelete(b.id)}
                        >
                          <i className="ri-delete-bin-line"></i> Delete
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </CardBody>
          </Card>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Upload Home Banner</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Label>Image <span className="text-danger">*</span></Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
                required
              />
            </div>
            <div className="mb-3">
              <Label>Link URL (optional)</Label>
              <Input
                type="url"
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggle}>Cancel</Button>
              <Button type="submit" color="primary" disabled={submitting}>
                {submitting ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default HomeBanner;
